// Supplier-admin pricing-update workflow. Three tabs:
//   1. Quick Bump  — apply a % to mat/lab across selected tiers
//   2. Upload      — drop a CSV/XLSX with the new prices
//   3. Export      — download current prices to edit in Excel
// All flows funnel through a single DiffPreview so the supplier reviews
// every cell before any write happens.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Download, Percent, RefreshCw, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingUpdatePanel({ token }) {
  const [tab, setTab] = useState("bump");        // "bump" | "upload" | "export"
  const [tiers, setTiers] = useState([]);
  const [changes, setChanges] = useState(null);  // staged preview
  const [bumpPct, setBumpPct] = useState(null);  // set when the staged preview came from Quick Bump
  const [unmatched, setUnmatched] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/tiers`, { headers: { "X-Admin-Token": token } });
        setTiers(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      }
    })();
  }, [token]);

  const applyChanges = useCallback(async (confirmedKeys, bumpOk) => {
    if (!changes?.length) return;
    setBusy(true);
    try {
      // TRANSPOSITION GATE (ruled 2026-07-31): rows past ×3 carry the
      // human's explicit per-row confirm — the server re-checks live.
      // THIRD DOOR: a bump past 15% carries its one confirm.
      const keyOf = (c) => `${c.tier_id}-${c.section}-${c.name}-${c.field}`;
      const payload = changes.map((c) => ({
        ...c,
        confirmed: confirmedKeys instanceof Set ? confirmedKeys.has(keyOf(c)) : false,
      }));
      const { data } = await axios.post(
        `${API}/admin/pricing/apply`,
        { changes: payload, bump_pct: bumpPct, bump_confirmed: bumpOk === true },
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`Applied ${data.applied} price ${data.applied === 1 ? "change" : "changes"}`);
      setChanges(null);
      setUnmatched([]);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.magnitude_gate) {
        toast.error(`${d.magnitude_gate.length} row(s) move past ×3 — confirm each red row before applying`);
      } else if (d?.bump_gate) {
        toast.error(d.message || `A ${d.bump_gate.pct}% bump exceeds the 15% line — confirm it first`);
      } else {
        toast.error(typeof d === "string" ? d : e.message);
      }
    } finally {
      setBusy(false);
    }
  }, [changes, token, bumpPct]);

  return (
    <div className="card p-6 mt-6" data-testid="pricing-update-panel">
      <div className="flex items-center gap-3 mb-2">
        <RefreshCw className="w-5 h-5 text-[var(--brand-text)]" />
        <div className="section-tag">Pricing Updates</div>
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        Bulk-update catalog prices when Alside raises rates. All changes preview as a diff
        before anything is saved — review the table and click Apply when you&apos;re ready.
      </p>

      {/* Tab bar */}
      <div className="inline-flex border border-[var(--border)] rounded-sm overflow-hidden text-sm font-bold uppercase tracking-wider mb-5">
        {[
          { key: "bump", label: "Quick Bump", icon: <Percent className="w-3.5 h-3.5" /> },
          { key: "upload", label: "Upload CSV/Excel", icon: <Upload className="w-3.5 h-3.5" /> },
          { key: "export", label: "Export", icon: <Download className="w-3.5 h-3.5" /> },
        ].map((t, i) => (
          <button
            key={t.key}
            type="button"
            className={`px-4 py-2 flex items-center gap-2 transition ${i > 0 ? "border-l border-[var(--border)]" : ""} ${
              tab === t.key ? "bg-[var(--bar-bg)] text-white" : "bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--bg-app)]"
            }`}
            onClick={() => setTab(t.key)}
            data-testid={`pricing-tab-${t.key}`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === "bump" && (
        <BumpForm tiers={tiers} token={token} setChanges={setChanges} setUnmatched={setUnmatched} setBumpPct={setBumpPct} />
      )}
      {tab === "upload" && (
        <UploadForm token={token} setChanges={setChanges} setUnmatched={setUnmatched} setBumpPct={setBumpPct} />
      )}
      {tab === "export" && <ExportPanel token={token} />}

      {/* Diff preview (shared across bump + upload) */}
      {changes !== null && (
        <DiffPreview
          changes={changes}
          unmatched={unmatched}
          bumpPct={bumpPct}
          onCancel={() => { setChanges(null); setUnmatched([]); setBumpPct(null); }}
          onApply={applyChanges}
          busy={busy}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Bump form
// ---------------------------------------------------------------------------
function BumpForm({ tiers, token, setChanges, setUnmatched, setBumpPct }) {
  const [percent, setPercent] = useState(4.5);
  const [target, setTarget] = useState("mat");
  const [tierIds, setTierIds] = useState([]);  // [] = all
  const [busy, setBusy] = useState(false);

  const toggleTier = (id) => {
    setTierIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const preview = async () => {
    if (!percent || isNaN(Number(percent))) {
      toast.error("Enter a percentage (e.g. 4.5 for +4.5%)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/pricing/preview-bump`,
        {
          percent: Number(percent),
          target,
          scope: { tier_ids: tierIds.length ? tierIds : null, section_titles: null },
        },
        { headers: { "X-Admin-Token": token } },
      );
      setChanges(data.changes || []);
      setUnmatched([]);
      setBumpPct(Number(percent));
      if (!data.changes?.length) {
        toast("No changes — current prices already match.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="label">Percentage change</label>
          <div className="flex items-stretch">
            <input
              type="number"
              step="0.1"
              className="input num"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              data-testid="bump-percent"
            />
            <div className="px-3 flex items-center bg-[var(--surface-muted)] border border-l-0 border-[var(--border)] text-[var(--ink-2)] font-mono-num">%</div>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-1">
            Positive = increase. Negative = decrease.
          </p>
        </div>

        <div>
          <label className="label">Apply to</label>
          <select
            className="input"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="bump-target"
          >
            <option value="mat">Material only</option>
            <option value="lab">Labor only</option>
            <option value="both">Material + Labor</option>
          </select>
        </div>

        <div className="flex items-end">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={preview}
            disabled={busy}
            data-testid="bump-preview-btn"
          >
            {busy ? "Computing…" : "Preview Changes"}
          </button>
        </div>
      </div>

      <div>
        <label className="label">Tiers to update</label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTierIds([])}
            className={`px-3 py-1.5 border text-xs uppercase tracking-wider font-bold transition ${
              tierIds.length === 0
                ? "bg-[var(--bar-bg)] text-white border-[var(--border-strong)]"
                : "bg-[var(--surface)] text-[var(--ink-2)] border-[var(--border)] hover:border-[var(--border-strong)]"
            }`}
            data-testid="bump-tier-all"
          >
            All tiers
          </button>
          {tiers.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => toggleTier(t.id)}
              className={`px-3 py-1.5 border text-xs uppercase tracking-wider font-bold transition ${
                tierIds.includes(t.id)
                  ? "bg-[var(--brand)] text-[var(--on-brand)] border-[var(--brand)]"
                  : "bg-[var(--surface)] text-[var(--ink-2)] border-[var(--border)] hover:border-[var(--brand)]"
              }`}
              data-testid={`bump-tier-${t.name}`}
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------
function UploadForm({ token, setChanges, setUnmatched, setBumpPct }) {
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef();

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setBumpPct(null); // upload door — the bump gate does not apply
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", "false");
      const { data } = await axios.post(
        `${API}/admin/pricing/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data", "X-Admin-Token": token } },
      );
      setChanges(data.changes || []);
      setUnmatched(data.unmatched || []);
      if (!data.changes?.length && !data.unmatched?.length) {
        toast("File parsed — no changes detected.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--ink-2)]">
        Upload a <strong>CSV</strong> or <strong>Excel (.xlsx)</strong> file with the new prices.
        Required columns: <code className="font-mono-num text-xs">tier, section, name, unit, mat, lab</code>.
        Tip: <strong>Export</strong> the current pricing first to get the exact format.
      </p>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid="upload-pricing-input"
      />
      <button
        type="button"
        className="btn-primary"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="upload-pricing-btn"
      >
        <Upload className="w-4 h-4" />
        {busy ? "Reading file…" : "Choose CSV / Excel"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
function ExportPanel({ token }) {
  const download = async () => {
    // SEC-006 — Iter 78z++++: header-based auth. We can't use
    // window.location.href anymore (it can't attach the header), so we
    // fetch the CSV as a blob and synthesize a download link.
    try {
      const res = await axios.get(`${API}/admin/pricing/export`, {
        responseType: "blob",
        headers: { "X-Admin-Token": token },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `pricing-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Export failed");
    }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--ink-2)]">
        Download a CSV snapshot of every tier × section × item with current prices.
        Open in Excel, edit the <code className="font-mono-num text-xs">mat</code> and{" "}
        <code className="font-mono-num text-xs">lab</code> columns, save, and re-upload via the Upload tab.
      </p>
      <button
        type="button"
        className="btn-primary"
        onClick={download}
        data-testid="export-pricing-btn"
      >
        <Download className="w-4 h-4" />
        Download Current Pricing (CSV)
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diff preview (shared)
// ---------------------------------------------------------------------------
function DiffPreview({ changes, unmatched, bumpPct, onCancel, onApply, busy }) {
  const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
  const keyOf = (c) => `${c.tier_id}-${c.section}-${c.name}-${c.field}`;
  // TRANSPOSITION GATE (Howard ruled 2026-07-31): rows moving past ×3
  // render RED with the % named and need a per-row confirm click before
  // Apply unlocks. server-annotated; falls back to the same math.
  const isFlagged = (c) =>
    c.magnitude_flag ?? (c.old > 0 && (c.new <= 0 || c.new / c.old >= 3 || c.new / c.old <= 1 / 3));
  const [confirmed, setConfirmed] = React.useState(() => new Set());
  const toggleConfirm = (k) =>
    setConfirmed((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  const flagged = changes.filter(isFlagged);
  const unconfirmed = flagged.filter((c) => !confirmed.has(keyOf(c))).length;
  // CROSSED-PAIR DETECTOR (ruled 2026-07-31): among flagged rows of the
  // same tier+field, a pair whose price ORDER inverted looks TRANSPOSED
  // — the exact shape of the House Wrap ↔ RainDrop upload.
  const pairs = [];
  const byGroup = {};
  for (const c of flagged) {
    if (!(c.old > 0)) continue;
    const g = `${c.tier_name}|${c.field}`;
    (byGroup[g] = byGroup[g] || []).push(c);
  }
  for (const rows of Object.values(byGroup)) {
    for (let i = 0; i < rows.length; i++) {
      for (let j = i + 1; j < rows.length; j++) {
        if ((rows[i].old - rows[j].old) * (rows[i].new - rows[j].new) < 0) {
          pairs.push({ tier: rows[i].tier_name, a: rows[i].name, b: rows[j].name });
        }
      }
    }
  }
  const total = changes.length;
  // THIRD DOOR (ruled 2026-07-31): a bump past 15% needs ONE confirm —
  // real increases run 3–13%; 45% is a fat-finger, not a market.
  const bumpGated = bumpPct != null && Math.abs(bumpPct) > 15;
  const [bumpOk, setBumpOk] = React.useState(false);
  const locked = unconfirmed > 0 || (bumpGated && !bumpOk);
  const increases = changes.filter((c) => c.new > c.old).length;
  const decreases = changes.filter((c) => c.new < c.old).length;

  return (
    <div className="mt-6 border-2 border-[var(--brand)] bg-orange-50" data-testid="diff-preview">
      <div className="bg-[var(--brand)] text-[var(--on-brand)] px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-heading text-base">Review changes</div>
          <div className="text-xs opacity-90 mt-0.5">
            {total} change{total === 1 ? "" : "s"} · {increases} up · {decreases} down
            {unmatched.length > 0 && ` · ${unmatched.length} unmatched`}
            {flagged.length > 0 && ` · ${flagged.length} past ×3`}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-1.5 bg-[var(--surface)] text-[var(--ink-2)] border border-white hover:bg-[var(--bg-app)] text-sm font-bold uppercase tracking-wider flex items-center gap-1.5"
            onClick={onCancel}
            disabled={busy}
            data-testid="diff-cancel-btn"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1.5 bg-[var(--bar-bg)] text-white border border-[var(--border-strong)] hover:bg-black text-sm font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
            onClick={() => onApply(confirmed, bumpOk)}
            disabled={busy || total === 0 || locked}
            title={locked ? "Confirm the flagged items first" : undefined}
            data-testid="diff-apply-btn"
          >
            <Check className="w-3.5 h-3.5" /> {busy ? "Applying…" : unconfirmed > 0 ? `Confirm ${unconfirmed} red row${unconfirmed === 1 ? "" : "s"} first` : bumpGated && !bumpOk ? "Confirm the bump first" : `Apply ${total}`}
          </button>
        </div>
      </div>

      {bumpGated && (
        <div className="px-4 py-3 bg-amber-100 border-b-2 border-amber-500" data-testid="bump-gate-banner">
          <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-amber-900 cursor-pointer">
            <input
              type="checkbox"
              checked={bumpOk}
              onChange={(e) => setBumpOk(e.target.checked)}
              data-testid="bump-gate-confirm"
            />
            ⚠ {bumpPct > 0 ? "+" : ""}{bumpPct}% bump exceeds the 15% line — real increases run 3–13%. Confirm this is intentional.
          </label>
        </div>
      )}
      {pairs.length > 0 && (
        <div className="px-4 py-3 bg-red-200 border-b-2 border-red-500" data-testid="transposition-pair-banner">
          <div className="text-xs uppercase tracking-wider font-bold text-red-900">
            ⚠ These rows look CROSSED — their price order inverted:
          </div>
          <ul className="text-[11px] text-red-900 mt-1 font-bold">
            {pairs.slice(0, 6).map((p, i) => (
              <li key={i}>{p.a} ↔ {p.b} <span className="font-normal opacity-75">({p.tier})</span></li>
            ))}
            {pairs.length > 6 && <li>…and {pairs.length - 6} more</li>}
          </ul>
          <div className="text-[11px] text-red-900 mt-1">Check for a transposed entry before confirming.</div>
        </div>
      )}
      {flagged.length > 0 && (
        <div className="px-4 py-3 bg-red-100 border-b-2 border-red-400" data-testid="magnitude-gate-banner">
          <div className="text-xs uppercase tracking-wider font-bold text-red-800">
            ⚠ {flagged.length} row{flagged.length === 1 ? "" : "s"} move{flagged.length === 1 ? "s" : ""} more than ×3 — a real market swing, or a crossed/transposed entry?
          </div>
          <div className="text-[11px] text-red-900 mt-1">
            Confirm each red row to unlock Apply. Nothing saves past the threshold without your click.
          </div>
        </div>
      )}

      {unmatched.length > 0 && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200">
          <div className="text-xs uppercase tracking-wider font-bold text-red-700 mb-2">
            Unmatched rows (skipped)
          </div>
          <ul className="text-xs text-red-900 space-y-1 font-mono-num">
            {unmatched.slice(0, 10).map((u) => (
              <li key={`${u.row}-${u.tier || ""}-${u.name || ""}`}>
                Row {u.row}: {u.tier || "?"} · {u.section || "?"} · {u.name || "?"} — <em>{u.reason}</em>
              </li>
            ))}
            {unmatched.length > 10 && <li>…and {unmatched.length - 10} more</li>}
          </ul>
        </div>
      )}

      {total === 0 ? (
        <div className="p-6 text-sm text-[var(--ink-2)] text-center">
          No price changes to apply.
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface)] border-b border-[var(--border)] sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Section</th>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 text-center">Field</th>
                <th className="px-3 py-2 text-right">Old</th>
                <th className="px-3 py-2 text-right">New</th>
                <th className="px-3 py-2 text-right">Δ</th>
                <th className="px-3 py-2 text-center">Confirm</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--surface)]">
              {changes.map((c) => {
                const delta = c.new - c.old;
                const pct = c.pct ?? (c.old ? (delta / c.old) * 100 : 0);
                const flagged = isFlagged(c);
                const k = keyOf(c);
                return (
                  <tr
                    key={k}
                    className={`border-b ${flagged ? "bg-red-50 border-red-200" : "border-[var(--bg-app)] hover:bg-[var(--surface-muted)]"}`}
                    data-testid={flagged ? `magnitude-flagged-row-${c.name}-${c.field}` : undefined}
                  >
                    <td className="px-3 py-1.5 font-mono-num text-xs text-[var(--ink-2)]">{c.tier_name}</td>
                    <td className="px-3 py-1.5 text-xs text-[var(--ink-2)]">{c.section}</td>
                    <td className="px-3 py-1.5 text-xs">{c.name}</td>
                    <td className="px-3 py-1.5 text-center text-[10px] uppercase font-bold tracking-wider text-[var(--muted)]">{c.field}</td>
                    <td className="px-3 py-1.5 text-right font-mono-num text-xs text-[var(--muted)] line-through">{usd(c.old)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono-num text-xs font-bold ${flagged ? "text-red-700" : ""}`}>{usd(c.new)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono-num text-xs font-bold ${flagged ? "text-red-700" : delta > 0 ? "text-green-700" : "text-red-700"}`}>
                      {delta > 0 ? "+" : ""}{usd(delta)} <span className={flagged ? "" : "opacity-60"}>({pct > 0 ? "+" : ""}{pct.toFixed(1)}%)</span>
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {flagged && (
                        <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={confirmed.has(k)}
                            onChange={() => toggleConfirm(k)}
                            data-testid={`magnitude-confirm-${c.name}-${c.field}`}
                          />
                          ×3
                        </label>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
